'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import StructuredContent, {
  createClient,
  Schema as SchemaType,
  StructuredContentNode,
  StructuredContentProps,
} from '@nerdwallet/structured-content-core';
import baseSchema from '@nerdwallet/structured-content/schema';
import { transform } from '@nerdwallet/structured-content-core/tree-utils';
import baseComponents from '@nerdwallet/structured-content-nextjs/components';
import { NativeType as Type } from '@nerdwallet/currency/components/NativeType';
import { has } from '../lodash-functions';
import renderNode from './structured-content-debugger';
import { lpcSchema } from './lpc-schema';
import { lpcComponents } from './lpc-components';
import { renderNodeWithABTesting } from '../ab-testing';

const schema = { ...baseSchema, ...lpcSchema };

const components = { ...baseComponents, ...lpcComponents };

export const client = createClient({
  components: components as any,
  schema,
  codesplittingFunction: (asyncImport: any, exportName: string) =>
    dynamic(() =>
      asyncImport.then((mod: any) => ({
        default: mod[exportName],
      }))
    ),
});

// Filter schema to include only relevant components
const filterNextSchema = (SCschema: SchemaType, nextComponents: SchemaType) => {
  const keysToFilterBy = Object.keys(nextComponents);
  return Object.fromEntries(
    keysToFilterBy
      .filter((key) => Object.hasOwn(SCschema, key))
      .map((key) => [key, SCschema[key]])
  );
};

interface Props extends StructuredContentProps {
  noClearFix?: boolean;
  strictChecks?: boolean;
}

export const StructuredContentWrapper: React.FC<Props> = ({
  strictChecks = false,
  Primitive = Type,
  ...restProps
}) => {
  const modifiedSchema: SchemaType = useMemo(
    () => filterNextSchema(schema, components),
    []
  );

  const props = useMemo(
    () => ({
      Primitive,
      ...restProps,
    }),
    [Primitive, restProps]
  );

  const generateProps = useCallback(
    (componentSchema: SchemaType) => {
      if (strictChecks) {
        return { ...props };
      }

      const children = transform(
        props.children,
        componentSchema,
        (node: StructuredContentNode) => {
          if (
            !node ||
            (has(node, 'type') && !has(componentSchema, node.type))
          ) {
            const error = new Error(
              `${node.type} is not present in the structured content schema.`
            );
            error.name = 'SCComponentNotPresent';
            datadogRum.addError(error, {
              componentName: node.type,
              aggregationKey: 'SCComponentNotPresent',
              alertType: 'error',
            });

            return null;
          }
          return node;
        }
      );

      return {
        ...props,
        children,
      };
    },
    [strictChecks, props]
  );

  const shouldUseStructuredContentDebugger =
    process.env.NEXT_PUBLIC_STRUCTURED_CONTENT_DEBUGGER === 'true';

  const generateContent = useCallback(
    (generatedProps: any) => (
      <StructuredContent
        {...generatedProps}
        renderNode={
          shouldUseStructuredContentDebugger
            ? renderNode
            : renderNodeWithABTesting
        }
      />
    ),
    [shouldUseStructuredContentDebugger]
  );

  const generatedProps = generateProps(modifiedSchema);
  if (!generatedProps.children) {
    return null;
  }

  return generateContent(generatedProps);
};
